import { type RouterIds, RouteById, router, type LazyRoute } from '@frontend/weave-router';
import { RootRouteComponent } from './routes/__root';

// Generic to enforce that the route returned matches the route path
type LazyRouteFn<TRoutePath extends RouterIds> = () => Promise<LazyRoute<RouteById<TRoutePath>>>;

type RouterMap = {
  // __root__ is a special route that isn't lazy loaded, so we need to manually bind it
  // You could consider adding null to the returned type to have routes without rendering
  [K in Exclude<RouterIds, '__root__'>]: LazyRouteFn<K>;
};

const routerMap: RouterMap = {
  '/': () => import('./routes').then((d) => d.default),
  '/test_/error': () => import('./routes/test_.error').then((d) => d.default),
  '/test/$id': () => import('./routes/test.$id').then((d) => d.default),
  '/nr-auth-test/$id': () => import('./routes/nr-auth-test.$id').then((d) => d.default),

  '/home/dashboard': () => import('./routes/home/dashboard').then((d) => d.default),
  '/home/onboarding': () => import('./routes/home/onboarding').then((d) => d.default),
  '/home/partners': () => import('./routes/home/partners').then((d) => d.default),

  '/parent': () => import('./routes/parent').then((d) => d.default),
  '/x_/parent': () => import('./routes/x_/parent').then((d) => d.default),
  '/x_/parent/child': () => import('./routes/x_/parent.child').then((d) => d.default),
  '/x_/redirect_/1': () => import('./routes/x_/redirect_.1').then((d) => d.default),
  '/x_/redirect_/2': () => import('./routes/x_/redirect_.2').then((d) => d.default),

  '/analytics/': () => import('./routes/analytics').then((d) => d.default),
  '/analytics/call-intelligence/$id': () => import('./routes/analytics/call-intelligence.$id').then((d) => d.default),
  '/analytics/call-intelligence': () => import('./routes/analytics/call-intelligence').then((d) => d.default),
  '/analytics/call-intelligence/main': () => import('./routes/analytics/call-intelligence.main').then((d) => d.default),
  '/analytics/call-intelligence/follow-ups_/$type': () =>
    import('./routes/analytics/call-intelligence.follow-ups_.$type').then((d) => d.default),
  '/analytics/call-intelligence/service-issue_/$type': () =>
    import('./routes/analytics/call-intelligence.service-issue_.$type').then((d) => d.default),
  '/analytics/marketing-attribution': () => import('./routes/analytics/marketing-attribution').then((d) => d.default),
  '/analytics/marketing-attribution/main': () =>
    import('./routes/analytics/marketing-attribution.main').then((d) => d.default),
  '/analytics/marketing-attribution/campaign_/$number': () =>
    import('./routes/analytics/marketing-attribution.campaign_.$number').then((d) => d.default),
  '/analytics/practice': () => import('./routes/analytics/practice').then((d) => d.default),
  '/analytics/practice/main': () => import('./routes/analytics/practice.main').then((d) => d.default),
  '/analytics/practice/cancelled-appointments': () =>
    import('./routes/analytics/practice.cancelled-appointments').then((d) => d.default),
  '/analytics/practice/demographics': () => import('./routes/analytics/practice.demographics').then((d) => d.default),
  '/analytics/practice/hygiene-follow-up': () =>
    import('./routes/analytics/practice.hygiene-follow-up').then((d) => d.default),
  '/analytics/practice/hygiene-treatment': () =>
    import('./routes/analytics/practice.hygiene-treatment').then((d) => d.default),
  '/analytics/practice/new-patients': () => import('./routes/analytics/practice.new-patients').then((d) => d.default),
  '/analytics/practice/practitioner-analysis': () =>
    import('./routes/analytics/practice.practitioner-analysis').then((d) => d.default),
  '/analytics/practice/recaptured-patients': () =>
    import('./routes/analytics/practice.recaptured-patients').then((d) => d.default),
  '/analytics/practice/restorative-treatment': () =>
    import('./routes/analytics/practice.restorative-treatment').then((d) => d.default),
  '/analytics/practice/unscheduled-patients': () =>
    import('./routes/analytics/practice.unscheduled-patients').then((d) => d.default),
  '/analytics/roi': () => import('./routes/analytics/roi').then((d) => d.default),
  '/analytics/appointment': () => import('./routes/analytics/appointment').then((d) => d.default),
  '/analytics/auto-recall': () => import('./routes/analytics/auto-recall').then((d) => d.default),
  '/analytics/messaging': () => import('./routes/analytics/messaging').then((d) => d.default),
  '/analytics/phone': () => import('./routes/analytics/phone').then((d) => d.default),
  '/analytics/phone/main': () => import('./routes/analytics/phone.main').then((d) => d.default),
  '/analytics/phone/call-duration': () => import('./routes/analytics/phone.call-duration').then((d) => d.default),
  '/analytics/phone/call-volume': () => import('./routes/analytics/phone.call-volume').then((d) => d.default),
  '/analytics/reviews': () => import('./routes/analytics/reviews').then((d) => d.default),
  '/analytics/roi/bulk-text': () => import('./routes/analytics/roi.bulk-text').then((d) => d.default),
  '/analytics/roi/no-show-rate': () => import('./routes/analytics/roi.no-show-rate').then((d) => d.default),
  '/analytics/dashboard': () => import('./routes/analytics/dashboard').then((d) => d.default),
  '/analytics/roi/missed-call-text-conversion': () =>
    import('./routes/analytics/roi.missed-call-text-conversion').then((d) => d.default),
  '/analytics/roi/appointment-reminders': () =>
    import('./routes/analytics/roi.appointment-reminders').then((d) => d.default),

  '/bulk_/$subNav': () => import('./routes/bulk_/$subNav').then((d) => d.default),
  '/bulk_/$subNav_/$type': () => import('./routes/bulk_/$subNav_.$type').then((d) => d.default),
  '/bulk_/$subNav_/$type_/$id': () => import('./routes/bulk_/$subNav_.$type_.$id').then((d) => d.default),
  '/bulk_/$subNav_/$type_/$id_/$view': () => import('./routes/bulk_/$subNav_.$type_.$id_.$view').then((d) => d.default),

  '/calls/': () => import('./routes/calls/').then((d) => d.default),
  '/calls/team-presence': () => import('./routes/calls/team-presence').then((d) => d.default),
  '/calls/voicemails': () => import('./routes/calls/voicemails').then((d) => d.default),
  '/calls/voicemail-boxes/$mailboxId/messages/$messageId': () =>
    import('./routes/calls/voicemail-boxes.$mailboxId.messages.$messageId').then((d) => d.default),
  '/calls/call-history': () => import('./routes/calls/call-history').then((d) => d.default),
  '/calls/call-queue-stats': () => import('./routes/calls/call-queue-stats').then((d) => d.default),

  '/messages/$filter/new': () => import('./routes/messages/$filter.new').then((d) => d.default),
  '/messages/$filter': () => import('./routes/messages/$filter').then((d) => d.default),
  '/messages/$filter/$groupId_/$threadId': () =>
    import('./routes/messages/$filter.$groupId_.$threadId').then((d) => d.default),

  '/payments/invoices': () => import('./routes/payments/invoices').then((d) => d.default),
  '/payments/invoices_/$id': () => import('./routes/payments/invoices_.$id').then((d) => d.default),
  '/payments/payouts': () => import('./routes/payments/payouts').then((d) => d.default),
  '/payments/refunds': () => import('./routes/payments/refunds').then((d) => d.default),
  '/payments/refunds_/$invoiceId': () => import('./routes/payments/refunds_.$invoiceId').then((d) => d.default),
  '/payments/payment-plans': () => import('./routes/payments/payment-plans').then((d) => d.default),
  '/payments/payment-plans_/$planId': () => import('./routes/payments/payment-plans_.$planId').then((d) => d.default),
  '/payments/payment-plans_/create': () => import('./routes/payments/payment-plans_.create').then((d) => d.default),

  '/reviews/$site': () => import('./routes/reviews/$site').then((d) => d.default),
  '/reviews/$site/$reviewId': () => import('./routes/reviews/$site.$reviewId').then((d) => d.default),

  '/fax/$filter/$faxId': () => import('./routes/fax/$filter.$faxId').then((d) => d.default),
  '/fax/$filter': () => import('./routes/fax/$filter').then((d) => d.default),
  '/fax/drafts/$draftId': () => import('./routes/fax/drafts_/$draftId').then((d) => d.default),
  '/fax/drafts/$draftId/begin': () => import('./routes/fax/drafts_/$draftId.begin').then((d) => d.default),
  '/fax/drafts/$draftId/cover': () => import('./routes/fax/drafts_/$draftId.cover').then((d) => d.default),
  '/fax/drafts/$draftId/upload': () => import('./routes/fax/drafts_/$draftId.upload').then((d) => d.default),
  '/fax/drafts/new-from-template': () => import('./routes/fax/drafts_/new-from-template').then((d) => d.default),
  '/fax/drafts/new': () => import('./routes/fax/drafts_/new').then((d) => d.default),
  '/fax/drafts/template-editor': () => import('./routes/fax/drafts_/template-editor').then((d) => d.default),
  '/fax/drafts/$draftId/finalize': () => import('./routes/fax/drafts_/$draftId.finalize').then((d) => d.default),

  '/contacts/lists': () => import('./routes/contacts/lists').then((d) => d.default),
  '/contacts/$filter': () => import('./routes/contacts/$filter').then((d) => d.default),

  '/fax/drafts/$draftId/choose-template': () =>
    import('./routes/fax/drafts_/$draftId.choose-template').then((d) => d.default),

  '/forms': () => import('./routes/forms').then((d) => d.default),
  '/forms/digitization': () => import('./routes/forms/digitization').then((d) => d.default),
  '/forms/kiosk': () => import('./routes/forms/kiosk').then((d) => d.default),
  '/forms/kiosk/patient-kiosk': () => import('./routes/forms/kiosk.patient-kiosk').then((d) => d.default),
  '/forms/library': () => import('./routes/forms/library').then((d) => d.default),
  '/forms/library/forms': () => import('./routes/forms/library.forms').then((d) => d.default),
  '/forms/library/packets': () => import('./routes/forms/library.packets').then((d) => d.default),
  '/forms/library/packets_/$id': () => import('./routes/forms/library.packets_.$id').then((d) => d.default),
  '/forms/builder_/$id': () => import('./routes/forms/builder_.$id').then((d) => d.default),
  '/forms/library/forms_/$id': () => import('./routes/forms/library.forms_.$id').then((d) => d.default),
  '/forms/submissions': () => import('./routes/forms/submissions').then((d) => d.default),
  '/forms/submissions/archived': () => import('./routes/forms/submissions.archived').then((d) => d.default),
  '/forms/submissions/inbox': () => import('./routes/forms/submissions.inbox').then((d) => d.default),
  '/forms/submissions/sent': () => import('./routes/forms/submissions.sent').then((d) => d.default),
  '/forms/templates': () => import('./routes/forms/templates').then((d) => d.default),
  '/forms/submissions/sent/$submissionId': () =>
    import('./routes/forms/submissions.sent.$submissionId').then((d) => d.default),
  '/forms/submissions/inbox/sync-manually': () =>
    import('./routes/forms/submissions.inbox.sync-manually').then((d) => d.default),
  '/forms/submissions/inbox/sync-manually/$submissionId': () =>
    import('./routes/forms/submissions.inbox.sync-manually.$submissionId').then((d) => d.default),
  '/forms/submissions/inbox/reviewed': () => import('./routes/forms/submissions.inbox.reviewed').then((d) => d.default),
  '/forms/submissions/inbox/reviewed/$submissionId': () =>
    import('./routes/forms/submissions.inbox.reviewed.$submissionId').then((d) => d.default),
  '/forms/submissions/inbox/retry-sync': () =>
    import('./routes/forms/submissions.inbox.retry-sync').then((d) => d.default),
  '/forms/submissions/inbox/retry-sync/$submissionId': () =>
    import('./routes/forms/submissions.inbox.retry-sync.$submissionId').then((d) => d.default),
  '/forms/submissions/inbox/needs-review/$submissionId': () =>
    import('./routes/forms/submissions.inbox.needs-review.$submissionId').then((d) => d.default),
  '/forms/submissions/inbox/needs-review': () =>
    import('./routes/forms/submissions.inbox.needs-review').then((d) => d.default),
  '/forms/submissions/inbox/completed/$submissionId': () =>
    import('./routes/forms/submissions.inbox.completed.$submissionId').then((d) => d.default),
  '/forms/submissions/inbox/completed': () =>
    import('./routes/forms/submissions.inbox.completed').then((d) => d.default),
  '/forms/submissions/inbox/all': () => import('./routes/forms/submissions.inbox.all').then((d) => d.default),
  '/forms/submissions/inbox/all/$submissionId': () =>
    import('./routes/forms/submissions.inbox.all.$submissionId').then((d) => d.default),
  '/forms/submissions/archived/$submissionId': () =>
    import('./routes/forms/submissions.archived.$submissionId').then((d) => d.default),

  '/schedule/calendar': () => import('./routes/schedule/calendar').then((d) => d.default),
  '/schedule/debug': () => import('./routes/schedule/debug').then((d) => d.default),
  '/schedule/request-history': () => import('./routes/schedule/request-history').then((d) => d.default),
  '/schedule/quickfill-history': () => import('./routes/schedule/quickfill-history').then((d) => d.default),
  '/analytics/phone/phone-tree-call-volume-all': () =>
    import('./routes/analytics/phone.phone-tree-call-volume-all').then((d) => d.default),

  '/onboard': () => import('./routes/onboard').then((d) => d.default),
  '/onboarding': () => import('./routes/onboarding').then((d) => d.default),
  '/onboarding/form': () => import('./routes/onboarding/form').then((d) => d.default),
  '/weave-admin/roles': () => import('./routes/weave-admin/roles').then((d) => d.default),
  '/payments/payouts_/$payoutId': () => import('./routes/payments/payouts_.$payoutId').then((d) => d.default),
  '/weave-copilot': () => import('./routes/weave-copilot').then((d) => d.default),
  '/analytics/ai-receptionist': () => import('./routes/analytics/ai-receptionist').then((d) => d.default),
  '/payments/payouts_/$payoutId_/$locationId': () =>
    import('./routes/payments/payouts_.$payoutId_.$locationId').then((d) => d.default),

  '/onboarding/form/$locationId_/$section': () =>
    import('./routes/onboarding/form.$locationId_.$section').then((d) => d.default),
  '/onboarding/form/$locationId_/complete': () =>
    import('./routes/onboarding/form.$locationId_.complete').then((d) => d.default),
  '/onboarding/form/$locationId_/dashboard': () =>
    import('./routes/onboarding/form.$locationId_.dashboard').then((d) => d.default),
  '/onboarding/form/$locationId_/$section_/$task_/$step': () =>
    import('./routes/onboarding/form.$locationId_.$section_.$task_.$step').then((d) => d.default),

  '/_public/onboarding-demo': () => import('./routes/_public/onboarding-demo').then((d) => d.default),
  '/_public/onboarding-demo/form': () => import('./routes/_public/onboarding-demo/form').then((d) => d.default),
  '/_public/onboarding-demo/form/$locationId_/$section': () =>
    import('./routes/_public/onboarding-demo/form.$locationId_.$section').then((d) => d.default),
  '/_public/onboarding-demo/form/$locationId_/complete': () =>
    import('./routes/_public/onboarding-demo/form.$locationId_.complete').then((d) => d.default),
  '/_public/onboarding-demo/form/$locationId_/dashboard': () =>
    import('./routes/_public/onboarding-demo/form.$locationId_.dashboard').then((d) => d.default),
  '/_public/onboarding-demo/form/$locationId_/$section_/$task_/$step': () =>
    import('./routes/_public/onboarding-demo/form.$locationId_.$section_.$task_.$step').then((d) => d.default),

  '/_public/login': () => import('./routes/_public/login').then((d) => d.default),
  '/_public/sign-in': () => import('./routes/_public/sign-in').then((d) => d.default),
  '/_public/sign-in_/callback': () => import('./routes/_public/sign-in_.callback').then((d) => d.default),

  '/_public/nr-public-test/$id': () => import('./routes/_public/nr-public-test.$id').then((d) => d.default),
  '/_public/test_/redirect_/1': () => import('./routes/_public/test_/redirect_.1').then((d) => d.default),
  '/_public/test_/redirect_/2': () => import('./routes/_public/test_/redirect_.2').then((d) => d.default),

  '/_public/reset-password_/$nonceToken': () =>
    import('./routes/_public/reset-password_/$nonceToken').then((d) => d.default),
  '/_public/password-reset': () => import('./routes/_public/password-reset').then((d) => d.default),

  '/_public/accept-invite_/$nonceToken_/location_/$locationId': () =>
    import('./routes/_public/accept-invite_/$nonceToken_.location_.$locationId').then((d) => d.default),
  '/_public/user-invite_/$nonceToken_/location_/$locationId': () =>
    import('./routes/_public/user-invite_/$nonceToken_.location_.$locationId').then((d) => d.default),
  '/_public/insurance-eligibility-report': () =>
    import('./routes/_public/insurance-eligibility-report').then((d) => d.default),
  '/_public/front-channel-logout': () => import('./routes/_public/front-channel-logout').then((d) => d.default),
  '/_public/hydra-error': () => import('./routes/_public/hydra-error').then((d) => d.default),
  '/_public/portal/accept-invite_/$nonceToken_/location_/$locationId': () =>
    import('./routes/_public/portal/accept-invite_.$nonceToken_.location_.$locationId').then((d) => d.default),
  '/_public/portal/login': () => import('./routes/_public/portal/login').then((d) => d.default),
  '/_public/portal/user-invite_/$nonceToken_/location_/$locationId': () =>
    import('./routes/_public/portal/user-invite_.$nonceToken_.location_.$locationId').then((d) => d.default),
  '/_public/it-partner-quotes': () => import('./routes/_public/it-partner-quotes').then((d) => d.default),
  '/_public/it-partner-quotes_/$quoteId_/pdf': () =>
    import('./routes/_public/it-partner-quotes_/$quoteId_.pdf').then((d) => d.default),
  '/_public/quotes_/$quoteId': () => import('./routes/_public/quotes_/$quoteId').then((d) => d.default),
  '/_public/quotes_/$quoteId_/pdf': () => import('./routes/_public/quotes_/$quoteId_.pdf').then((d) => d.default),
  '/_public/porting_/loa_/$loaId': () => import('./routes/_public/porting_/loa_.$loaId').then((d) => d.default),
  '/_public/porting_/loa_/$loaId_/pdf': () =>
    import('./routes/_public/porting_/loa_.$loaId_.pdf').then((d) => d.default),
  '/_public/reset': () => import('./routes/_public/reset').then((d) => d.default),
  '/_public/okta-redirect': () => import('./routes/_public/okta-redirect').then((d) => d.default),
};

export const initializeRouter = () => {
  // don't lazy load the root component. Set the component here manually
  router.routesById['__root__'].update({
    component: RootRouteComponent,
  });

  // set the lazy load component on each route
  Object.entries(routerMap).forEach(([path, component]) => {
    const foundRoute = router.routesById[path as RouterIds];
    // @ts-ignore -- ignore until we can fix the type -- TODO: Needs to be fixed and removed.
    foundRoute.lazy(component);
  });

  return router;
};
